<script setup>
import { onBeforeUnmount, onMounted, ref } from "vue";

const canvasRef = ref(null);
let animationFrame = 0;
let resizeObserver = null;
let backgroundImage = null;

const dotPositions = [
  { x: 0.06, y: 0.10, r: 3.5 },
  { x: 0.94, y: 0.16, r: 4.5 },
  { x: 0.10, y: 0.42, r: 3.5 },
  { x: 0.90, y: 0.58, r: 3.5 },
  { x: 0.15, y: 0.78, r: 3.5 },
  { x: 0.85, y: 0.88, r: 4.5 },
  { x: 0.22, y: 0.28, r: 3.0 },
  { x: 0.78, y: 0.35, r: 3.0 },
  { x: 0.08, y: 0.60, r: 3.0 },
  { x: 0.92, y: 0.72, r: 3.0 },
  { x: 0.40, y: 0.15, r: 2.5 },
  { x: 0.60, y: 0.85, r: 2.5 }
];

const orbitDots = [
  { x: 0.22, y: 0.18, r: 2.8, orbitX: 38, orbitY: 26, speed: 0.90, phase: 0.2 },
  { x: 0.82, y: 0.22, r: 3.2, orbitX: 44, orbitY: 32, speed: 0.78, phase: 1.4 },
  { x: 0.15, y: 0.72, r: 2.4, orbitX: 26, orbitY: 38, speed: 1.02, phase: 2.1 },
  { x: 0.80, y: 0.80, r: 3.0, orbitX: 34, orbitY: 28, speed: 0.85, phase: 3.3 }
];

function resizeCanvas() {
  const canvas = canvasRef.value;
  if (!canvas) return;
  const rect = canvas.getBoundingClientRect();
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const width = Math.max(1, Math.round(rect.width));
  const height = Math.max(1, Math.round(rect.height));
  canvas.width = Math.round(width * dpr);
  canvas.height = Math.round(height * dpr);
  canvas.dataset.logicalWidth = String(width);
  canvas.dataset.logicalHeight = String(height);
}

function drawCoverImage(ctx, image, width, height) {
  const imageRatio = image.naturalWidth / image.naturalHeight;
  const targetRatio = width / height;
  let sx = 0;
  let sy = 0;
  let sw = image.naturalWidth;
  let sh = image.naturalHeight;

  if (targetRatio > imageRatio) {
    sh = image.naturalWidth / targetRatio;
    sy = (image.naturalHeight - sh) / 2;
  } else {
    sw = image.naturalHeight * targetRatio;
    sx = (image.naturalWidth - sw) / 2;
  }
  ctx.drawImage(image, sx, sy, sw, sh, 0, 0, width, height);
}

function drawFrame() {
  const canvas = canvasRef.value;
  if (!canvas) return;
  const width = Number(canvas.dataset.logicalWidth) || canvas.clientWidth;
  const height = Number(canvas.dataset.logicalHeight) || canvas.clientHeight;
  const dpr = canvas.width / Math.max(width, 1);
  const ctx = canvas.getContext("2d");
  const now = performance.now() / 1000;
  const motionScale = height / width > 1.3 ? 1.45 : 1;

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, width, height);

  if (backgroundImage?.complete && backgroundImage.naturalWidth > 0) {
    drawCoverImage(ctx, backgroundImage, width, height);
  } else {
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, "#082640");
    gradient.addColorStop(1, "#03111f");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  }

  const shade = ctx.createLinearGradient(0, 0, 0, height);
  shade.addColorStop(0, "rgba(1, 10, 20, 0.08)");
  shade.addColorStop(0.52, "rgba(0, 8, 17, 0.18)");
  shade.addColorStop(1, "rgba(0, 5, 13, 0.36)");
  ctx.fillStyle = shade;
  ctx.fillRect(0, 0, width, height);

  // 原主页两侧漂移的青色装饰线。
  ctx.save();
  const linePositions = [0.12, 0.28, 0.44, 0.60, 0.76, 0.92];
  linePositions.forEach((ratio, index) => {
    const baseY = height * ratio;
    const phase = index * 1.2;
    const floatY = baseY + Math.sin(now * 0.4 + phase) * 40;
    const breath = 0.05 + 0.40 * (0.5 + 0.5 * Math.sin(now * 0.5 + phase * 1.3));
    const lengthScale = 0.3 + 1.7 * (0.5 + 0.5 * Math.sin(now * 0.35 + phase * 0.7));

    ctx.globalAlpha = breath;
    ctx.strokeStyle = "rgba(72, 236, 238, 0.9)";
    ctx.lineWidth = 1.8;
    ctx.shadowColor = "#52f6e8";
    ctx.shadowBlur = breath * 20;
    ctx.beginPath();
    ctx.moveTo(10, floatY);
    ctx.lineTo(10 + 70 * lengthScale, floatY);
    ctx.stroke();

    const rightPhase = phase + 1.8;
    const rightFloatY = baseY + Math.sin(now * 0.4 + rightPhase) * 40;
    const rightLen = 70 * (0.3 + 1.7 * (0.5 + 0.5 * Math.sin(now * 0.35 + rightPhase * 0.7)));
    ctx.beginPath();
    ctx.moveTo(width - 10, rightFloatY + 40);
    ctx.lineTo(width - 10 - rightLen, rightFloatY + 40);
    ctx.stroke();
  });
  ctx.restore();

  // 原主页会漂移、会呼吸变大的泡泡/光点。
  ctx.save();
  for (const dot of dotPositions) {
    const baseX = width * dot.x;
    const baseY = height * dot.y;
    const phase = dot.x * 5 + dot.y * 3;
    const speed = 0.36 + (dot.x + dot.y) * 0.18;
    const x = baseX + (Math.cos(now * speed + phase * 1.3) * 52 + Math.sin(now * 0.78 + phase * 0.7) * 18) * motionScale;
    const y = baseY + (Math.sin(now * speed * 1.1 + phase * 1.1) * 46 + Math.cos(now * 0.62 + phase * 1.5) * 12) * motionScale;
    const pulse = 0.15 + 2.85 * (0.5 + 0.5 * Math.sin(now * 0.60 + phase * 1.5));
    const radius = Math.max(1.5, dot.r * pulse);
    const alpha = 0.22 + 0.76 * (0.5 + 0.5 * Math.sin(now * 0.55 + phase * 1.2));

    ctx.globalAlpha = alpha * 0.88;
    ctx.fillStyle = "rgba(200, 255, 255, 0.96)";
    ctx.shadowColor = "#52f6e8";
    ctx.shadowBlur = 14 * pulse + 10;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }

  for (const orb of orbitDots) {
    const centerX = width * orb.x;
    const centerY = height * orb.y;
    const theta = now * orb.speed + orb.phase;
    const x = centerX + (Math.cos(theta) * orb.orbitX + Math.sin(theta * 1.4) * 8) * motionScale;
    const y = centerY + (Math.sin(theta) * orb.orbitY + Math.cos(theta * 1.2) * 6) * motionScale;
    const radius = orb.r + Math.sin(now * 1.6 + orb.phase) * 0.7 * motionScale;
    const alpha = 0.28 + 0.62 * (0.5 + 0.5 * Math.sin(now * 1.3 + orb.phase * 0.9));

    ctx.globalAlpha = alpha;
    ctx.fillStyle = "rgba(180, 248, 255, 0.95)";
    ctx.shadowColor = "rgba(82,246,232,0.95)";
    ctx.shadowBlur = 16;
    ctx.beginPath();
    ctx.arc(x, y, Math.max(1, radius), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  animationFrame = requestAnimationFrame(drawFrame);
}

onMounted(() => {
  backgroundImage = new Image();
  backgroundImage.src = "/assets/beijing.jpg";
  backgroundImage.addEventListener("load", () => {
    if (!animationFrame) drawFrame();
  }, { once: true });

  resizeCanvas();
  resizeObserver = new ResizeObserver(resizeCanvas);
  if (canvasRef.value) resizeObserver.observe(canvasRef.value);
  window.addEventListener("resize", resizeCanvas);
  animationFrame = requestAnimationFrame(drawFrame);
});

onBeforeUnmount(() => {
  cancelAnimationFrame(animationFrame);
  animationFrame = 0;
  resizeObserver?.disconnect();
  window.removeEventListener("resize", resizeCanvas);
});
</script>

<template>
  <canvas ref="canvasRef" class="home-background" aria-hidden="true"></canvas>
</template>

<style scoped>
.home-background {
  position: fixed;
  inset: 0;
  z-index: 0;
  width: 100vw;
  height: 100dvh;
  pointer-events: none;
}
</style>
